import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import TimeSeriesGraph from "../timeSeriesGraph/index";
// Labels moved to i18n; use translation keys instead of mgaLabels
import MGAAPI from "../../api/index";
import displayError from "../../constants/utils";
import { Grid, Select, Switch, Text, TextInput } from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import PropTypes from "prop-types";
import _ from "lodash";
import { capitalizeFirstLetterAndSplitName, formatInputValue, mgaRegex, responsiveCol } from "../../constants/helper";
import { useAtom } from "jotai";
import { saveDisabledAtom } from "../../utils/atoms/saveDisabledAtom";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom";
import { helpAtom } from "../../utils/atoms/helpAtom";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";
import { formatProjectNumber } from "../../utils/helpers";
import ProjectNumberInput from "../common/ProjectNumberInput";

const MGA_API = new MGAAPI();

const parsePositiveNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsedValue = Number.parseFloat(value);
  if (!Number.isFinite(parsedValue)) return null;
  return parsedValue;
};

const parseValidScale = (value) => {
  const parsedValue = parsePositiveNumber(value);
  if (parsedValue === null || parsedValue <= 0) return null;
  return parsedValue;
};

const getSeriesMax = (series = []) => {
  let maxValue = 0;
  for (const point of series) {
    const numericPoint = Number(point);
    if (Number.isFinite(numericPoint)) {
      maxValue = Math.max(maxValue, numericPoint);
    }
  }
  return maxValue;
};

const computeSeriesStats = (series = []) => {
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let count = 0;
  for (const point of series) {
    const num = Number(point);
    if (!Number.isFinite(num)) continue;
    if (num < min) min = num;
    if (num > max) max = num;
    sum += num;
    count++;
  }
  if (!count) return { min: 0, max: 0, mean: 0, energy: 0 };
  const mean = sum / count;
  // Matches TimeSeriesGraph's default 15-minute interval assumption.
  const energy = sum * (15 / 60);
  return { min, max, mean, energy };
};

const formatStoredPowerValue = (value) => {
  if (!Number.isFinite(value)) return "";
  return Number(value.toFixed(2)).toString();
};

const formatDisplayPowerValue = (value) => {
  if (!Number.isFinite(value)) return "";
  return Number(value.toFixed(2)).toString();
};

const getUnitMultiplier = (unit) => (unit === "kW" ? 1000 : 1);

const getScaledSeries = (baseSeries = [], scaleValue, maxPowerValue) => {
  const validScale = parseValidScale(scaleValue);
  if (!validScale) {
    return { series: [], scaledMax: 0 };
  }

  const scaledSeries = _.map(baseSeries, (point) => Number(point) * validScale);
  const scaledMax = getSeriesMax(scaledSeries);
  const parsedMaxPower = parsePositiveNumber(maxPowerValue);

  if (parsedMaxPower === null || scaledMax <= 0) {
    return { series: scaledSeries, scaledMax };
  }
  if (parsedMaxPower === 0) {
    return {
      series: _.map(scaledSeries, () => 0),
      scaledMax: 0,
    };
  }

  const multiplier = parsedMaxPower / scaledMax;
  return {
    series: _.map(scaledSeries, (point) => point * multiplier),
    scaledMax: parsedMaxPower,
  };
};

const ASHRAELoad = ({
  componentData,
  getBuildingTypes,
  selectedBuildingType,
  displayLoader,
  ashraeData,
  projectDetails,
  setDisplayLoader,
  form,
  updateLoadState,
  loadParameters,
}) => {
  const { t } = useTranslation();
  const numberFormat = projectDetails?.projectDTO?.numberFormat;
  const [, setSaveDisabled] = useAtom(saveDisabledAtom);
  const [ASHRAEDataAssest, setASHRAEDataAssest] = useState(ashraeData);
  const [initialData, setInitialData] = useState(null);
  const ashraeContainerRef = useRef(null);
  const [helpMenuData] = useAtom(helpContextMenuAtom);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const [maxPowerDraft, setMaxPowerDraft] = useState({
    maxPowerElec: null,
    maxPowerTherm: null,
  });
  const profileDetail = loadParameters?.componentAttributes?.loadParameters?.profileDetail || {};
  const isThermalLoadEnabled = profileDetail?.useThermalLoad ?? true;
  const scalingFactorValue = profileDetail?.scalingFactor ?? 1;
  const electricScaleValue = loadParameters?.componentAttributes?.loadParameters?.scalingFactorElec ?? profileDetail?.scalingFactor ?? 1;
  const thermalScaleValue = loadParameters?.componentAttributes?.loadParameters?.scalingFactorTherm ?? profileDetail?.scalingFactor ?? 1;
  const electricMaxPowerValue = profileDetail?.maxPowerElec;
  const thermalMaxPowerValue = profileDetail?.maxPowerTherm;

  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.load?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
    };
  };
  useHelpFocusBlur(ashraeContainerRef, setHelperAtom, helperAtom);

  const resetSaveDisabledForAshrae = () => {
    setSaveDisabled(false);
  };

  useEffect(()=>{
    resetSaveDisabledForAshrae();
  },[])

  const [stats, setStats] = useState({
    electrical: { max: 0, min: 0, mean: 0, energy: 0 },
    thermal: { max: 0, min: 0, mean: 0, energy: 0 },
  });
  const [displayUnit, setDisplayUnit] = useState("W");
  // Persist full-year stats once; ignore later zoom-driven updates
  const fullYearStatsSetRef = useRef(false);
  const unitMultiplier = useMemo(() => getUnitMultiplier(displayUnit), [displayUnit]);

  const scaledProfiles = useMemo(() => {
    const electricalBaseSeries = initialData?.ProfileElec?.[1] || [];
    const thermalBaseSeries = initialData?.ProfileTherm?.[1] || [];
    const electricalProfile = getScaledSeries(
      electricalBaseSeries,
      electricScaleValue,
      electricMaxPowerValue
    );
    const thermalProfile = getScaledSeries(
      thermalBaseSeries,
      isThermalLoadEnabled ? thermalScaleValue : null,
      isThermalLoadEnabled ? thermalMaxPowerValue : null
    );

    return {
      electrical: electricalProfile,
      thermal: thermalProfile,
    };
  }, [electricMaxPowerValue, electricScaleValue, initialData, isThermalLoadEnabled, thermalMaxPowerValue, thermalScaleValue]);

  const displayedElectricMaxPower = useMemo(() => {
    if (maxPowerDraft.maxPowerElec !== null) return maxPowerDraft.maxPowerElec;
    if (electricMaxPowerValue === "") return "";
    const resolvedValue = parsePositiveNumber(electricMaxPowerValue);
    if (resolvedValue !== null) {
      return formatDisplayPowerValue(resolvedValue / unitMultiplier);
    }
    return formatDisplayPowerValue(scaledProfiles.electrical.scaledMax / unitMultiplier);
  }, [electricMaxPowerValue, maxPowerDraft.maxPowerElec, scaledProfiles.electrical.scaledMax, unitMultiplier]);

  const displayedThermalMaxPower = useMemo(() => {
    if (maxPowerDraft.maxPowerTherm !== null) return maxPowerDraft.maxPowerTherm;
    if (thermalMaxPowerValue === "") return "";
    const resolvedValue = parsePositiveNumber(thermalMaxPowerValue);
    if (resolvedValue !== null) {
      return formatDisplayPowerValue(resolvedValue / unitMultiplier);
    }
    return formatDisplayPowerValue(scaledProfiles.thermal.scaledMax / unitMultiplier);
  }, [maxPowerDraft.maxPowerTherm, scaledProfiles.thermal.scaledMax, thermalMaxPowerValue, unitMultiplier]);

  const getAshraeData = (value) => {
    setDisplayLoader(true);
    MGA_API.ashraeBuildingTypeProjectDetails(
      projectDetails.projectDTO.projectId,
      value
    )
      .then((res) => {
        // New building type -> reset full-year stats latch so fresh stats can be captured
        fullYearStatsSetRef.current = false;
        setDisplayUnit("W");
        setStats({
          electrical: { max: 0, min: 0, mean: 0, energy: 0 },
          thermal: { max: 0, min: 0, mean: 0, energy: 0 },
        });
        setInitialData(res);
        setDisplayLoader(false);
      })
      .catch((res) => {
        setDisplayLoader(false);
        displayError("error", res.response.data);
      });
  };

  const handleChartStats = (chartStats) => {
    // chartStats: { electrical: {min,max,mean,energy}, thermal?: {...} }
    const normalizeStat = (v) => {
      if (v == null || isNaN(v)) return 0;
      return Math.abs(Number(v));
    };
    
    // Update stats only if at least one series is available
    if (!chartStats || (!chartStats.electrical && !chartStats.thermal)) return;
    
    // Only set once for the overall year; subsequent zoom changes should not update these displayed values
    setStats((prev) => {
      if (fullYearStatsSetRef.current) {
        return prev;
      }
      
      const next = {
        electrical: chartStats.electrical
          ? {
              max: normalizeStat(chartStats.electrical.max),
              min: normalizeStat(chartStats.electrical.min),
              mean: normalizeStat(chartStats.electrical.mean),
              energy: normalizeStat(chartStats.electrical.energy),
            }
          : prev.electrical,
        thermal: chartStats.thermal
          ? {
              max: normalizeStat(chartStats.thermal.max),
              min: normalizeStat(chartStats.thermal.min),
              mean: normalizeStat(chartStats.thermal.mean),
              energy: normalizeStat(chartStats.thermal.energy),
            }
          : prev.thermal,
      };
      
      // Mark as set when we have valid electrical stats
      if (
        (chartStats.electrical && (chartStats.electrical.max !== 0 || chartStats.electrical.energy !== 0)) ||
        (chartStats.thermal && (chartStats.thermal.max !== 0 || chartStats.thermal.energy !== 0))
      ) {
        fullYearStatsSetRef.current = true;
      }
      
      return next;
    });
  };

  const handleUnitChange = (unit) => {
    setDisplayUnit(unit);
    fullYearStatsSetRef.current = false;
    setStats({
      electrical: { max: 0, min: 0, mean: 0, energy: 0 },
      thermal: { max: 0, min: 0, mean: 0, energy: 0 },
    });
  };

  const buildingTypeDropdownOptions =
    getBuildingTypes && getBuildingTypes?.length > 0
      ? getBuildingTypes?.map((type) => ({
          value: type,
          label: capitalizeFirstLetterAndSplitName(type),
        }))
      : [];

  const defaultbuildingTypeOptions = [
    {
      value: t('ashrae.noBuildingType'),
      label: capitalizeFirstLetterAndSplitName(t('ashrae.noBuildingType')),
      disabled: true,
    },
  ];

  const buildingTypeOptions = [
    ...buildingTypeDropdownOptions,
    ...defaultbuildingTypeOptions,
  ];

  const resetChartStats = () => {
    fullYearStatsSetRef.current = false;
    setStats({
      electrical: { max: 0, min: 0, mean: 0, energy: 0 },
      thermal: { max: 0, min: 0, mean: 0, energy: 0 },
    });
  };

  const syncMaxPowerForScale = (fieldKey, scaleValue, baseSeries) => {
    const validScale = parseValidScale(scaleValue);
    const nextMaxPowerValue = validScale
      ? formatStoredPowerValue(getSeriesMax(_.map(baseSeries || [], (point) => Number(point) * validScale)))
      : "0";

    updateLoadState(
      "componentAttributes.loadParameters.profileDetail",
      fieldKey,
      nextMaxPowerValue
    );
  };

  const handleScaleChange = (fieldKey, maxPowerFieldKey, baseSeries, value) => {
    // Determine parent path based on field type
    const isScalingFactorField = fieldKey === "scalingFactorElec" || fieldKey === "scalingFactorTherm";
    const scaleParentPath = isScalingFactorField ? "componentAttributes.loadParameters" : "componentAttributes.loadParameters.profileDetail";
    
    if (value === "") {
      updateLoadState(
        scaleParentPath,
        fieldKey,
        value
      );
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        maxPowerFieldKey,
        ""
      );
      resetChartStats();
      return;
    }

    if (!mgaRegex?.scaleInputRegex?.test(value)) return;

    const numericValue = Number.parseFloat(value);
    const hasDecimal = value.includes('.');
    const endsWithDecimal = value.endsWith('.');

    if (Number.isNaN(numericValue)) return;

    let normalizedValue = value;
    if (!endsWithDecimal) {
      const parts = value.split('.');
      parts[0] = String(Number.parseInt(parts[0], 10));
      normalizedValue = parts.join('.');
    }

    if (endsWithDecimal) {
      updateLoadState(
        scaleParentPath,
        fieldKey,
        normalizedValue
      );
      return;
    }

    if (hasDecimal && (numericValue < 0.10 || numericValue > 9.90)) {
      return;
    }

    if (!hasDecimal && numericValue > 9.90) {
      return;
    }

    updateLoadState(
      scaleParentPath,
      fieldKey,
      normalizedValue
    );
    syncMaxPowerForScale(maxPowerFieldKey, normalizedValue, baseSeries);
  };

  const handleMasterScaleChange = (value) => {
    if (value === "") {
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "scalingFactor",
        value
      );
      updateLoadState(
        "componentAttributes.loadParameters",
        "scalingFactorElec",
        value
      );
      updateLoadState(
        "componentAttributes.loadParameters",
        "scalingFactorTherm",
        value
      );
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "maxPowerElec",
        ""
      );
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "maxPowerTherm",
        ""
      );
      resetChartStats();
      return;
    }

    if (!mgaRegex?.scaleInputRegex?.test(value)) return;

    const numericValue = Number.parseFloat(value);
    const hasDecimal = value.includes('.');
    const endsWithDecimal = value.endsWith('.');

    if (Number.isNaN(numericValue)) return;

    // Normalize leading zeros (e.g., "02" -> "2", "02.5" -> "2.5")
    let normalizedValue = value;
    if (!endsWithDecimal) {
      const parts = value.split('.');
      parts[0] = String(Number.parseInt(parts[0], 10));
      normalizedValue = parts.join('.');
    }

    if (endsWithDecimal) {
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "scalingFactor",
        normalizedValue
      );
      updateLoadState(
        "componentAttributes.loadParameters",
        "scalingFactorElec",
        normalizedValue
      );
      updateLoadState(
        "componentAttributes.loadParameters",
        "scalingFactorTherm",
        normalizedValue
      );
      return;
    }

    // if (hasDecimal && (numericValue < 0.1 || numericValue > 9.9)) return;
    // if (!hasDecimal && numericValue > 9.9) return;

    updateLoadState(
      "componentAttributes.loadParameters.profileDetail",
      "scalingFactor",
      normalizedValue
    );
    updateLoadState(
      "componentAttributes.loadParameters",
      "scalingFactorElec",
      normalizedValue
    );
    updateLoadState(
      "componentAttributes.loadParameters",
      "scalingFactorTherm",
      normalizedValue
    );
    syncMaxPowerForScale("maxPowerElec", normalizedValue, initialData?.ProfileElec?.[1]);
    syncMaxPowerForScale("maxPowerTherm", normalizedValue, initialData?.ProfileTherm?.[1]);
  };

  const handleMaxPowerChange = (fieldKey, value) => {
    const formattedValue = formatInputValue(value);

    if (formattedValue !== "" && !mgaRegex?.maxPowerInputRegex?.test(formattedValue)) return;

    setMaxPowerDraft((prev) => ({
      ...prev,
      [fieldKey]: formattedValue,
    }));

    if (formattedValue === "") {
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        fieldKey,
        ""
      );
      resetChartStats();
      return;
    }

    if (formattedValue.endsWith('.')) {
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        fieldKey,
        formattedValue
      );
      return;
    }

    const numericValue = Number.parseFloat(formattedValue);
    if (!Number.isFinite(numericValue) || numericValue < 0 || numericValue > 1000000000) {
      return;
    }

    const nativeValue = numericValue * unitMultiplier;
    if (!mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(String(nativeValue))) {
      return;
    }

    // Max power cannot exceed the base (unscaled) profile's max value multiplied
    // by the max allowed scale factor (9.9).
    const baseSeries =
      fieldKey === "maxPowerElec"
        ? initialData?.ProfileElec?.[1] || []
        : initialData?.ProfileTherm?.[1] || [];
    const baseSeriesMax = getSeriesMax(baseSeries);
    const maxAllowedNativeValue = baseSeriesMax * 9.9;
    if (baseSeriesMax > 0 && nativeValue > maxAllowedNativeValue) {
      return;
    }

    updateLoadState(
      "componentAttributes.loadParameters.profileDetail",
      fieldKey,
      formatStoredPowerValue(nativeValue)
    );

    // Auto-recalculate scale factor based on max power
    if (baseSeriesMax > 0) {
      const newScaleFactor = nativeValue / baseSeriesMax;
      const formattedScale = Number(newScaleFactor.toFixed(2)).toString();
      updateLoadState(
        "componentAttributes.loadParameters",
        fieldKey === "maxPowerElec" ? "scalingFactorElec" : "scalingFactorTherm",
        formattedScale
      );
    }
  };

  const handleMaxPowerBlur = (fieldKey) => {
    const draftValue = maxPowerDraft[fieldKey];
    if (typeof draftValue === "string" && draftValue.trim().endsWith(".")) {
      return;
    }
    setMaxPowerDraft((prev) => ({
      ...prev,
      [fieldKey]: null,
    }));
  };

  const handleThermalLoadToggle = (checked) => {
    updateLoadState(
      "componentAttributes.loadParameters.profileDetail",
      "useThermalLoad",
      checked
    );
  };

  useEffect(() => {
    setASHRAEDataAssest({
      ProfileElec: [[], scaledProfiles.electrical.series],
      ProfileTherm: [[], isThermalLoadEnabled ? scaledProfiles.thermal.series : []],
    });
  }, [isThermalLoadEnabled, scaledProfiles]);

  // Compute displayed stats directly from the exact series fed into the graph,
  // so the stats box always matches the graph whenever scaling, max power,
  // thermal toggle, or the display unit changes (independent of chart zoom).
  useEffect(() => {
    const normalizeStat = (v) => {
      if (v == null || isNaN(v)) return 0;
      return Math.abs(Number(v));
    };

    const electricalStats = computeSeriesStats(
      _.map(scaledProfiles.electrical.series, (point) => Number(point) / unitMultiplier)
    );
    const thermalStats = isThermalLoadEnabled
      ? computeSeriesStats(_.map(scaledProfiles.thermal.series, (point) => Number(point) / unitMultiplier))
      : { min: 0, max: 0, mean: 0, energy: 0 };

    fullYearStatsSetRef.current = true;
    setStats({
      electrical: {
        max: normalizeStat(electricalStats.max),
        min: normalizeStat(electricalStats.min),
        mean: normalizeStat(electricalStats.mean),
        energy: normalizeStat(electricalStats.energy),
      },
      thermal: {
        max: normalizeStat(thermalStats.max),
        min: normalizeStat(thermalStats.min),
        mean: normalizeStat(thermalStats.mean),
        energy: normalizeStat(thermalStats.energy),
      },
    });
  }, [scaledProfiles, isThermalLoadEnabled, unitMultiplier]);

  useEffect(() => {
    if (!initialData) return;
    syncMaxPowerForScale("maxPowerElec", electricScaleValue, initialData?.ProfileElec?.[1]);
    if (isThermalLoadEnabled) {
      syncMaxPowerForScale("maxPowerTherm", thermalScaleValue, initialData?.ProfileTherm?.[1]);
    }
  }, [initialData, electricScaleValue, isThermalLoadEnabled, thermalScaleValue]);

  useEffect(() => {
    const ashraeBuildingType =
      loadParameters?.componentAttributes?.loadParameters?.profileDetail
        ?.profileDataLoc;
    if (ashraeBuildingType) {
      getAshraeData(ashraeBuildingType);
    }
  }, [loadParameters?.componentAttributes?.loadParameters?.profileDetail
        ?.profileDataLoc]);
        

  return (
    <div ref={ashraeContainerRef}>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col span={3}>
          <Select
           {...getFieldAttributes("field-6")}
            data-id="field-6"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t('ashrae.buildingType')}
            data={buildingTypeOptions}
            value={
              loadParameters?.componentAttributes?.loadParameters?.profileDetail
                ?.profileDataLoc
            }
            required
            placeholder={t('ashrae.selectBuildingType')}
            withinPortal
            data-testid="building-type"
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.profileDataLoc"
            )}
            onChange={(value) => {
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "profileDataLoc",
                value
              );
              updateLoadState(
                "componentAttributes.loadParameters",
                "scalingFactorElec",
                "1"
              );
              updateLoadState(
                "componentAttributes.loadParameters",
                "scalingFactorTherm",
                "1"
              );
            }}
            searchable
          />
        </Grid.Col>
        {/* <Grid.Col span={3}>
          <TextInput
           {...getFieldAttributes("field-7")}
            data-id="field-7"
            label={t('ashrae.scalingLabel')}
            type="text"
            id="scalingFactor"
            placeholder="0.1 - 9.9"
            value={scalingFactorValue ?? ""}
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.scalingFactor"
            )}
            onChange={(event) => {
              const {
                target: { value },
              } = { ...event };
              handleMasterScaleChange(value);
            }}
          />
        </Grid.Col> */}
               <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          onClick={e => e.stopPropagation()}
          {...getFieldAttributes("field-11")}
          data-id="field-11"
          label={t('ashrae.electricalMaxPower')}
          type="text"
          {...form.getInputProps(
            "componentAttributes.loadParameters.profileDetail.maxPowerElec"
          )}
          value={displayedElectricMaxPower}
          onChange={(event) => {
            handleMaxPowerChange("maxPowerElec", event.currentTarget.value);
          }}
          onBlur={() => handleMaxPowerBlur("maxPowerElec")}
          rightSection={
            <div>
              {displayUnit}
            </div>
          }
          rightSectionProps={{ style: { pointerEvents: "none" } }}
        />
       </Grid.Col>
       <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
           {...getFieldAttributes("field-7")}
            data-id="field-7"
            label={t('ashrae.electricScale')}
            type="text"
            id="electricScale"
            {...form.getInputProps(
              "componentAttributes.loadParameters.scalingFactorElec"
            )}
            value={electricScaleValue ?? ""}
            onChange={(event) => {
              const {
                target: { value },
              } = { ...event };
              handleScaleChange(
                "scalingFactorElec",
                "maxPowerElec",
                initialData?.ProfileElec?.[1],
                value
              );
            }}
            required
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              height: "100%",
              gap:'3%'
            }}
          >
            <se-icon size="medium" style={{ paddingBottom: "1.5%" }}>
              energy
            </se-icon>
            <div>
              <Text>{t('ashrae.max')}: {formatProjectNumber(stats?.electrical?.max || 0, { numberFormat, minimumFractionDigits: 2, maximumFractionDigits: 2 })} {displayUnit}</Text>
              <Text>{t('ashrae.min')}: {formatProjectNumber(stats?.electrical?.min || 0, { numberFormat, minimumFractionDigits: 2, maximumFractionDigits: 2 })} {displayUnit}</Text>
            </div>
            <div>
              <Text>{t('ashrae.mean')}: {formatProjectNumber(stats?.electrical?.mean || 0, { numberFormat, minimumFractionDigits: 2, maximumFractionDigits: 2 })} {displayUnit}</Text>
              <Text>{t('ashrae.energy')}: {formatProjectNumber(stats?.electrical?.energy || 0, { numberFormat, minimumFractionDigits: 2, maximumFractionDigits: 2 })} {displayUnit}h</Text>
            </div>
          </div>
        </Grid.Col>
      </Grid>
      <Grid gutter="md" className="mt-2 mb-2">
         <Grid.Col {...responsiveCol}>
          <Switch
            label={t('ashrae.thermalLoad')}
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.useThermalLoad"
            )}
            color="seGreen.0"
            checked={isThermalLoadEnabled}
            onChange={(event) => {
              handleThermalLoadToggle(event.currentTarget.checked);
            }}
          />
        </Grid.Col>
        </Grid>
       {isThermalLoadEnabled && (<>
      <Grid gutter="md" className="mt-2 mb-2">
         <>
          <Grid.Col {...responsiveCol}>
            <ProjectNumberInput
              onClick={e => e.stopPropagation()}
              {...getFieldAttributes("field-12")}
              data-id="field-12"
              label={t('ashrae.thermalMaxPower')}
              type="text"
              {...form.getInputProps(
                "componentAttributes.loadParameters.profileDetail.maxPowerTherm"
              )}
              value={displayedThermalMaxPower}
              onChange={(event) => {
                handleMaxPowerChange("maxPowerTherm", event.currentTarget.value);
              }}
              onBlur={() => handleMaxPowerBlur("maxPowerTherm")}
              rightSection={
                <div>
                  {displayUnit}
                </div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
            />
           </Grid.Col>
           <Grid.Col {...responsiveCol}>
            <ProjectNumberInput
              {...getFieldAttributes("field-7")}
              data-id="field-7"
              label={t('ashrae.thermalScale')}
              type="text"
              id="thermalScale"
              {...form.getInputProps(
                "componentAttributes.loadParameters.scalingFactorTherm"
              )}
              value={thermalScaleValue ?? ""}
              onChange={(event) => {
                const {
                  target: { value },
                } = { ...event };
                handleScaleChange(
                  "scalingFactorTherm",
                  "maxPowerTherm",
                  initialData?.ProfileTherm?.[1],
                  value
                );
              }}
              required
            />
           </Grid.Col>
           <Grid.Col span={3}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              height: "100%",
              gap:'3%'
            }}
          >
            <se-icon size="medium" style={{ paddingBottom: "1.5%" }}>
              thermal_loads
            </se-icon>
            <div>
              <Text>{t('ashrae.max')}: {formatProjectNumber(stats?.thermal?.max || 0, { numberFormat, minimumFractionDigits: 2, maximumFractionDigits: 2 })} {displayUnit}</Text>
              <Text>{t('ashrae.mean')}: {formatProjectNumber(stats?.thermal?.mean || 0, { numberFormat, minimumFractionDigits: 2, maximumFractionDigits: 2 })} {displayUnit}</Text>
            </div>
            <div>
              <Text>{t('ashrae.min')}: {formatProjectNumber(stats?.thermal?.min || 0, { numberFormat, minimumFractionDigits: 2, maximumFractionDigits: 2 })} {displayUnit}</Text>
              <Text>{t('ashrae.energy')}: {formatProjectNumber(stats?.thermal?.energy || 0, { numberFormat, minimumFractionDigits: 2, maximumFractionDigits: 2 })} {displayUnit}h</Text>
            </div>
          </div>
         </Grid.Col>
         </>
      </Grid>
      <Grid gutter="md" className="mt-2 mb-2">
         <Grid.Col {...responsiveCol}>
            <ProjectNumberInput
              onClick={e => e.stopPropagation()}
              {...getFieldAttributes("field-8")}
              data-id="field-8"
              label={t('loadAttributes.thermalEnergyPrice')}
              required
              {...form.getInputProps(
                "componentAttributes.loadParameters.profileDetail.ThermalEnergyPrice"
              )}
              onChange={(e) => {
                const trimmedValue = formatInputValue(e.currentTarget.value);
                updateLoadState(
                  "componentAttributes.loadParameters.profileDetail",
                  "ThermalEnergyPrice",
                  trimmedValue
                );
              }}
              rightSection={
                <div style={{padding: "4px",}}>
                  {projectDetails?.projectDTO?.currencyCode || "$"}/kWh
                </div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
              rightSectionWidth={60}
            />
           </Grid.Col>
           <Grid.Col {...responsiveCol}>
              <ProjectNumberInput
                onClick={e => e.stopPropagation()}
                {...getFieldAttributes("field-9")}
                data-id="field-9"
                label={t('loadAttributes.thermalEmissionFactor')}
                required
                {...form.getInputProps(
                  "componentAttributes.loadParameters.profileDetail.ThermalEmissionFactor"
                )}
                onChange={(e) => {
                  const trimmedValue = formatInputValue(e.currentTarget.value);
                  updateLoadState(
                    "componentAttributes.loadParameters.profileDetail",
                    "ThermalEmissionFactor",
                    trimmedValue
                  );
                }}
                 rightSection={
                  <div style={{padding: "4px", marginRight: "1rem" }}>
                    gCO2/kWh
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                rightSectionWidth={60}
              />
            </Grid.Col>
            </Grid>
            </>)}
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col span={12}>
          {displayLoader && <se-loading loading={displayLoader}></se-loading>}
          {(ASHRAEDataAssest?.ProfileElec?.[1]?.length || ASHRAEDataAssest?.ProfileTherm?.[1]?.length) &&
          loadParameters?.componentAttributes?.loadParameters?.profileDetail
            ?.profileDataLoc &&
          ASHRAEDataAssest ? (
            <TimeSeriesGraph
              dataAssset={ASHRAEDataAssest?.ProfileElec?.[1]}
              dataAssset2={isThermalLoadEnabled ? ASHRAEDataAssest?.ProfileTherm?.[1] : []}
              color={"#ff6384"}
              name="Ashrae"
              componentData={componentData}
              yAxisName="W"
              embedHeight={500}
              showMinimap={true}
              minimapGap={40}
              lazyLoad={true}
              onStats={handleChartStats}
              onUnitChange={handleUnitChange}
            />
          ) : null}
        </Grid.Col>
      </Grid>
    </div>
  );
};

ASHRAELoad.propTypes = {
  componentData: PropTypes.object,
  getBuildingTypes: PropTypes.array,
  selectedBuildingType: PropTypes.string,
  displayLoader: PropTypes.bool,
  ashraeData: PropTypes.object,
  projectDetails: PropTypes.object,
  setDisplayLoader: PropTypes.func,
  form: PropTypes.object,
  loadParameters: PropTypes.object,
  updateLoadState: PropTypes.func,
};
export default ASHRAELoad;
